import { AdminScope } from '@pubstack/common/src/scope'
import { AdminSite } from '@pubstack/common/src/site'
import { sortByName } from '@pubstack/common/src/sort'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from '../api-client'

export const ADMIN_SITES_QUERY_KEYS = (scopeId: string) => ['admin', 'scopes', scopeId, 'tags']

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useAdminSite = (scopeId: string, siteId?: string) => {
  return useQuery<AdminSite>({ url: `/admin/scopes/${scopeId}/sites/${siteId}`, keys: [...ADMIN_SITES_QUERY_KEYS(scopeId), siteId ?? ''], enabled: !!scopeId && !!siteId })
}

export const useAdminSites = (scopeId: AdminScope['id'], search?: string) => {
  const url = buildUrlWithSearchParams(`/admin/scopes/${scopeId}/sites`, { search })
  const queryResult = useQuery<AdminSite[]>({ url, keys: [...ADMIN_SITES_QUERY_KEYS(scopeId), { search }], enabled: !!scopeId, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

export const useAllAdminSites = (search?: string) => {
  const url = buildUrlWithSearchParams(`/admin/sites`, { search })
  const queryResult = useQuery<AdminSite[]>({ url, keys: [...ADMIN_SITES_QUERY_KEYS('all'), { search }], keepPreviousDataEnabled: true, select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateAdminSite = (scopeId: AdminScope['id'], toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'post',
    url: `/admin/scopes/${scopeId}/sites`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    ...toasts,
  })
}

export const useUpdateAdminSite = (scopeId: AdminScope['id'], siteId: string, isOptimistic: boolean, toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'put',
    url: `/admin/scopes/${scopeId}/sites/${siteId}`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    resourceId: siteId,
    isOptimistic,
    ...toasts,
  })
}

export const useDeleteAdminSite = (scopeId: AdminScope['id'], siteId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdminSite>({
    method: 'delete',
    url: `/admin/scopes/${scopeId}/sites/${siteId}`,
    collectionKeys: ADMIN_SITES_QUERY_KEYS(scopeId),
    resourceId: siteId,
    ...toasts,
  })
}
